import { Ref, forwardRef, useMemo } from "react"
import { Form, Formik, FormikProps } from "formik"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { Admin } from "../../../../../users/userTypes"
import { CreateUpdateTenantAdminContent } from "./CreateUpdateTenantAdminContent"
import { useLocationState } from "../../../../../../core/router/routerHooks"
import * as Yup from "yup"
import { isPossiblePhoneNumber } from "libphonenumber-js"
import { CreateEditTenantAdminFormValues, CreateOrUpdateAdminState } from "../tenantAdminTypes"

type CreateOrEditTenantAdminFormFormProps = Readonly<{
    adminType: Admin["type"]
    existingAdmin?: Admin
    onSubmit: (formData: CreateEditTenantAdminFormValues) => void
}>
type FormikRef = Ref<FormikProps<CreateEditTenantAdminFormValues>>

export const CreateOrEditTenantAdminForm = forwardRef(
    ({ onSubmit, adminType, existingAdmin }: CreateOrEditTenantAdminFormFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const { user: userItem } = useLocationState<CreateOrUpdateAdminState>({ user: undefined })

        const mode = userItem ? "update" : "create"

        function getInitialValues(user?: Admin): CreateEditTenantAdminFormValues {
            return {
                username: user?.user.username ?? "",
                displayName: user?.user?.displayName ?? "",
                email: user?.user?.email ?? "",
                mobileNumber: user?.user?.mobileNumber ?? "",
                allowToCreateAdmins: user?.securitySettings?.allowToCreateAdmins ?? true,
                restrictAccessToIpAddresses: user?.securitySettings?.restrictAccessToIpAddresses ?? "",
                pinCode: user?.securitySettings?.pinCode?.value ?? "",
                isActive: user?.user.isActive ?? true,
                roles: user?.roles ?? [],
            }
        }

        function getValidationSchema() {
            return Yup.object().shape({
                username: Yup.string(),
                displayName: Yup.string().required(l10n.getString("office-manager-create-user-required-field-empty")),
                email: Yup.string()
                    .email(l10n.getString("office-manager-create-user-invalid-email-address"))
                    .required(l10n.getString("office-manager-create-user-required-field-empty")),
                mobileNumber: Yup.string().test({
                    name: "is-phone-number",
                    test: (value) => (value ? isPossiblePhoneNumber(value, "DK") : true),
                    message: l10n.getString("office-manager-create-user-invalid-phone-number"),
                }),
                allowToCreateAdmins: Yup.boolean(),
                restrictAccessToIpAddresses: Yup.string(),
                pinCode: Yup.string().matches(/^\d{4}$/, {
                    message: l10n.getString("office-manager-security-pin-invalid"),
                    excludeEmptyString: true,
                }),
                isActive: Yup.boolean(),
                roles: Yup.array().of(Yup.string()),
            })
        }

        const initialValues = useMemo(() => getInitialValues(existingAdmin), [existingAdmin])

        const validationSchema = useMemo(() => getValidationSchema(), [])

        function handleSubmit(values: CreateEditTenantAdminFormValues) {
            onSubmit(values)
        }

        return (
            <Formik<CreateEditTenantAdminFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={ref}
            >
                <Form>
                    <CreateUpdateTenantAdminContent existingAdmin={existingAdmin} mode={mode} adminType={adminType} />
                </Form>
            </Formik>
        )
    },
)

